import { debounce } from './helpers';

/**
 * Universal Tab System Initializer
 *
 * Computes panel heights, handles smooth active swapping transitions,
 * manages ARIA attributes for accessibility, and prevents page jumping (layout shifting).
 *
 * @param {Object} options Configuration options for the tab system.
 * @param {string} options.tabsContainerSelector Selector for the tabs parent container.
 * @param {string} options.panelsContainerSelector Selector for the panels parent container.
 * @param {string} options.tabSelector Selector for each tab button.
 * @param {string} options.panelSelector Selector for each content panel.
 * @param {string} [options.bgImageSelector] Optional selector for background image element to cross-fade.
 * @param {string} [options.bgContainerSelector] Optional selector for background container.
 * @param {string} [options.titleSelector] Optional selector for a dynamic title text to cross-fade.
 * @param {string} [options.tabIndexAttribute='data-tab'] Attribute holding tab index.
 * @param {string} [options.panelIndexAttribute='data-panel'] Attribute holding panel index.
 * @param {Function} [options.onTabChange] Optional callback triggered after tab selection changes.
 */
export const initTabSystem = ({
  tabsContainerSelector,
  panelsContainerSelector,
  tabSelector,
  panelSelector,
  bgImageSelector,
  bgContainerSelector,
  titleSelector,
  tabIndexAttribute = 'data-tab',
  panelIndexAttribute = 'data-panel',
  onTabChange,
}) => {
  const tabsContainer = document.querySelector(tabsContainerSelector);
  const panelsContainer = document.querySelector(panelsContainerSelector);

  if (!tabsContainer || !panelsContainer) return;

  const tabs = tabsContainer.querySelectorAll(tabSelector);
  const panels = panelsContainer.querySelectorAll(panelSelector);

  if (!tabs.length || !panels.length) return;

  // ─── Recalculate max-height safely to prevent page layout jumps ───────
  const updateMinHeight = () => {
    let maxHeight = 0;
    const clones = [];

    // 1. Append all clones to the DOM (Write phase)
    panels.forEach((panel) => {
      const panelClone = panel.cloneNode(true);
      panelClone.style.visibility = 'hidden';
      panelClone.style.opacity = '0';
      panelClone.style.position = 'relative';
      panelClone.style.display = 'block'; // Make sure offsetHeight can be read
      panelsContainer.appendChild(panelClone);
      clones.push(panelClone);
    });

    // 2. Measure all heights (Read phase)
    clones.forEach((clone) => {
      const height = clone.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    // 3. Remove all clones from the DOM (Write phase)
    clones.forEach((clone) => {
      panelsContainer.removeChild(clone);
    });

    if (maxHeight > 0) {
      panelsContainer.style.minHeight = `${maxHeight}px`;
    }
  };

  // Run calculation in the next animation frame to prevent forced reflow on initial load
  requestAnimationFrame(() => {
    updateMinHeight();
  });

  // Debounce resize listener (150ms) to prevent window resize layout thrashing
  window.addEventListener('resize', debounce(updateMinHeight, 150));

  // Initialize initial tabindex and ARIA state
  tabs.forEach((tab) => {
    const isActive = tab.classList.contains('active');
    tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tab.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  panels.forEach((panel) => {
    const isActive = panel.classList.contains('active');
    panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
  });

  // ─── Tab Event Listeners ───────────────────────────────────────────────
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', function () {
      const tabIndex = this.getAttribute(tabIndexAttribute);
      const activePanel = panelsContainer.querySelector(
        `[${panelIndexAttribute}="${tabIndex}"]`,
      );

      if (!activePanel) return;

      const isAlreadyActive = activePanel.classList.contains('active');

      // Update tabs active states
      tabs.forEach((t) => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      });
      this.classList.add('active');
      this.setAttribute('aria-selected', 'true');
      this.setAttribute('tabindex', '0');

      // Update background and titles only if not already active to avoid redundant transition animations
      if (!isAlreadyActive) {
        const newImage = this.getAttribute('data-image');
        const newTitle = this.getAttribute('data-title');
        const bgImageElement = bgImageSelector
          ? document.querySelector(bgImageSelector)
          : null;
        const bgContainer = bgContainerSelector
          ? document.querySelector(bgContainerSelector)
          : null;
        const titleElement = titleSelector
          ? document.querySelector(titleSelector)
          : null;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
          // Update immediately without animations or timeouts
          if (bgImageElement && newImage) {
            bgImageElement.src = newImage;
            const newSrcset = this.getAttribute('data-srcset');
            if (newSrcset) {
              bgImageElement.srcset = newSrcset;
            } else {
              bgImageElement.removeAttribute('srcset');
            }
            bgImageElement.style.opacity = '1';
            if (bgContainer) bgContainer.style.display = 'block';
          } else if (bgContainer) {
            bgContainer.style.display = 'none';
          }

          if (titleElement && newTitle) {
            titleElement.textContent = newTitle;
            titleElement.style.opacity = '1';
          }

          panels.forEach((panel) => {
            if (panel.classList.contains('active')) {
              panel.classList.remove('active');
              panel.setAttribute('aria-hidden', 'true');
            }
          });

          activePanel.classList.add('active');
          activePanel.setAttribute('aria-hidden', 'false');
        } else {
          // Start fade out of the backgrounds/titles if they exist
          if (bgImageElement) {
            bgImageElement.style.transition = 'opacity 0.5s ease-in-out';
            bgImageElement.style.opacity = '0';
          }
          if (titleElement) {
            titleElement.style.transition = 'opacity 0.5s ease-in-out';
            titleElement.style.opacity = '0';
          }

          // Cross-fade delay to update content and fade back in
          setTimeout(() => {
            if (bgImageElement && newImage) {
              bgImageElement.src = newImage;
              const newSrcset = this.getAttribute('data-srcset');
              if (newSrcset) {
                bgImageElement.srcset = newSrcset;
              } else {
                bgImageElement.removeAttribute('srcset');
              }
              bgImageElement.style.opacity = '1';
              if (bgContainer) bgContainer.style.display = 'block';
            } else if (bgContainer) {
              bgContainer.style.display = 'none';
            }

            if (titleElement && newTitle) {
              titleElement.textContent = newTitle;
              titleElement.style.opacity = '1';
            }
          }, 250);

          // Fade out other panels
          panels.forEach((panel) => {
            if (panel.classList.contains('active')) {
              panel.classList.remove('active');
              panel.setAttribute('aria-hidden', 'true');
            }
          });

          // Fade in new panel after short delay
          setTimeout(() => {
            activePanel.classList.add('active');
            activePanel.setAttribute('aria-hidden', 'false');
          }, 50);
        }
      }

      // Execute optional callbacks (useful for page-specific visual animations or modals)
      if (typeof onTabChange === 'function') {
        onTabChange(activePanel, isAlreadyActive, this);
      }
    });

    // Keyboard Arrow Navigation
    tab.addEventListener('keydown', function (e) {
      let targetIndex = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        targetIndex = (index + 1) % tabs.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        targetIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        targetIndex = 0;
      } else if (e.key === 'End') {
        targetIndex = tabs.length - 1;
      }

      if (targetIndex !== null) {
        e.preventDefault();
        tabs[targetIndex].focus();
        tabs[targetIndex].click(); // Automatically trigger selection on focus
      }
    });
  });
};
